import { Dataset, DEFAULT_PAGE_SIZE, DtFilter, DtSort } from '@/components/data-table/helpers';
import {
  CreateResponse,
  DEFAULT_SERVICE_ERROR,
  DeleteResponse,
  FieldError,
  ListResponse,
  PatchResponse,
  SAFE_LIMIT,
  UpdateResponse,
} from '.';
import { create, deleteApi, get, patch, post, update } from './sp-api.service';
import { orderBy } from 'lodash';
import {
  ClassifierModel,
  ClassifierUsage,
  CreateClassifierModel,
  getTypedClassifier,
  IntentClassifierModel,
} from '../models/classifier';
import { downloadFile, JsonObject } from '../helpers';
import { getAllPoliciesV1 } from './policy-v1.service';
import { PolicyModelV1 } from '../models/policy-model-v1/policy-v1.model';
import { ClassifierEntity } from '../models/policy-model-v1/classifier-controls';
import { AxiosError } from 'axios';

const CLASSIFICATION_PATH = '/detect-intent-v3/classification';

type UploadFieldError = FieldError & { lines?: number[] };

export const getClassifiers = async (
  page = 0,
  pageSize = DEFAULT_PAGE_SIZE,
  dtSort?: DtSort,
  dtFilter?: DtFilter
): Promise<Dataset<ClassifierModel>> => {
  const response = (await post(`${CLASSIFICATION_PATH}/search`, {
    match: undefined,
    page: 1,
    pageSize: SAFE_LIMIT,
    count: true,
  })) as ListResponse;

  const { count, data } = response || {};

  if (!data) {
    return { page, pageSize, rows: [], total: 0 };
  }

  let rows = response.data.map((item) => getTypedClassifier(new ClassifierModel(item)));

  // Filter
  if (dtFilter) {
    const { keyword } = dtFilter;
    if (keyword) {
      const safeKeyword = String(keyword).trim().toLowerCase();
      rows = rows.filter(
        ({ name, description }) =>
          String(name).toLowerCase().includes(safeKeyword) ||
          String(description).toLowerCase().includes(safeKeyword)
      );
    }
  }

  // Sort
  if (dtSort) {
    const { columnName, direction } = dtSort;
    rows = orderBy(rows, [columnName], [direction]);
  }

  // Paginate
  const startIndex = page * pageSize;
  rows = rows.slice(startIndex, startIndex + pageSize);

  return {
    page,
    pageSize,
    rows,
    total: count,
  };
};

export const getAllClassifiers = async (): Promise<ClassifierModel[]> => {
  return getClassifiers(0, SAFE_LIMIT).then(({ rows }) => rows);
};

export const getClassifierById = async (id: string): Promise<ClassifierModel | null> => {
  const response = await get(`${CLASSIFICATION_PATH}/${id}`);
  return response ? getTypedClassifier(new ClassifierModel(response as JsonObject)) : null;
};

export const createClassifier = async (classifier: ClassifierModel): Promise<CreateResponse> => {
  const createModel = new CreateClassifierModel(classifier);

  const response = await create(CLASSIFICATION_PATH, createModel._props);

  if (!response?.created) {
    return response;
  }

  response.model = getTypedClassifier(new ClassifierModel(response.data as JsonObject));

  return response;
};

export const patchClassifier = async (classifier: ClassifierModel): Promise<PatchResponse> => {
  const createModel = new CreateClassifierModel(classifier);
  return patch(`${CLASSIFICATION_PATH}/${createModel.id}`, createModel._props);
};

export const deleteClassifier = async (id: string): Promise<DeleteResponse> => {
  return deleteApi(`${CLASSIFICATION_PATH}/${id}`);
};

export const getClassifierUsages = async (): Promise<Map<string, ClassifierUsage>> => {
  const policies = await getAllPoliciesV1();

  const classifierUsages: Map<string, ClassifierUsage> = new Map();

  const addUsages = (policy: PolicyModelV1, entities: ClassifierEntity[]) => {
    entities.forEach(({ classifierId, action }) => {
      if (action === 'ignore') {
        return; // Skip ignore actions
      }
      const mapItem = classifierUsages.get(classifierId) || {
        policies: [],
        inUse: true,
      };
      if (!mapItem.policies.some(({ id }) => id === policy.id)) {
        mapItem.policies.push(policy);
      }
      classifierUsages.set(classifierId, mapItem);
    });
  };

  policies.forEach((policy) => {
    addUsages(policy, policy.classifierControls.entities || []);
    addUsages(policy, policy.regexPatterns?.entities || []);
  });

  return classifierUsages;
};

export const uploadPhraseFile = (classifierId: string, file: File) => {
  const formData = new FormData();
  formData.append('file', file);

  return update(
    `${CLASSIFICATION_PATH}/${classifierId}/phrases/upload`,
    formData,
    {
      timeout: 1000 * 60 * 2,
      headers: { 'Content-Type': 'multipart/form-data' },
    },
    handleUpdateErrorResponse
  );
};

export const downloadPhrases = (classifier: IntentClassifierModel) => {
  const fileContent = classifier.phrases.join('\n');
  const blob = new Blob([fileContent], { type: 'text/plain' });

  const safeName = String(classifier.name)
    .toLowerCase()
    .replace(/[^a-z0-9]/gi, '-');
  const dateForFilename = new Date().toISOString().replace(/:/g, '-').replace(/\./g, '-');
  const filename = `f5-workforce-ai-security-classifier-phrases-${safeName}-${dateForFilename}.txt`;

  return downloadFile(blob, filename);
};

const handleUpdateErrorResponse = (error: AxiosError): UpdateResponse => {
  if (!error.response) {
    return { updated: false, error: DEFAULT_SERVICE_ERROR };
  }

  const { status, data } = error.response;

  if (status === 400 && Array.isArray(data)) {
    const fieldErrors = (data as UploadFieldError[]).map((fieldError) => {
      if (fieldError.lines?.length) {
        fieldError.msg = `${fieldError.msg} Please review the following line(s) from the uploaded file: ${fieldError.lines.join(', ')}`;
      }
      return fieldError;
    });

    return { updated: false, fieldErrors };
  }

  return { updated: false, error: DEFAULT_SERVICE_ERROR };
};
